import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Brain, BookOpen, Target, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import capeService, { TaxonomyNode, CAPEQuestion, CAPEContent } from '../../services/capeService';

// Styled Components
const CAPEIntegrationCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
  }

  /* Personalized content indicator */
  &::after {
    content: '🎯 PERSONALIZED';
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(46, 204, 113, 0.9);
    color: white;
    font-size: 8px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
`;

const CAPEHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const CAPETitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CAPEDescription = styled.p`
  margin: 0 0 15px 0;
  opacity: 0.9;
  font-size: 0.95rem;
`;

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin: 15px 0;
`;

const TopicCard = styled.div<{ selected?: boolean; loading?: boolean }>`
  background: ${props => 
    props.loading ? 'rgba(255, 255, 255, 0.1)' :
    props.selected ? 'rgba(46, 204, 113, 0.3)' : 'rgba(255, 255, 255, 0.15)'
  };
  border: 1px solid ${props => 
    props.selected ? 'rgba(46, 204, 113, 0.5)' : 'rgba(255, 255, 255, 0.2)'
  };
  border-radius: 10px;
  padding: 15px;
  cursor: ${props => props.loading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  opacity: ${props => props.loading ? 0.6 : 1};

  &:hover {
    background: ${props => props.loading ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.25)'};
    transform: ${props => props.loading ? 'none' : 'translateY(-2px)'};
  }
`;

const TopicTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
`;

const TopicMeta = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
  line-height: 1.4;
`;

const ActionButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.5)' : '#333'};
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  margin-top: 15px;
  width: 100%;

  &:hover {
    background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.2)' : 'white'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'loading' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin: 10px 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background: rgba(46, 204, 113, 0.2);
          border: 1px solid rgba(46, 204, 113, 0.4);
          color: #2ecc71;
        `;
      case 'error':
        return `
          background: rgba(231, 76, 60, 0.2);
          border: 1px solid rgba(231, 76, 60, 0.4);
          color: #e74c3c;
        `;
      case 'loading':
        return `
          background: rgba(52, 152, 219, 0.2);
          border: 1px solid rgba(52, 152, 219, 0.4);
          color: #3498db;
        `;
    }
  }}
`;

interface CAPEIntegrationProps {
  competitorId: string;
  competitorName: string;
  university?: string;
  courseOfStudy?: string;
  onChallengeStart?: (challengeData: {
    topic: TaxonomyNode;
    questions: CAPEQuestion[];
    content: CAPEContent[];
  }) => void;
}

const CAPEIntegration: React.FC<CAPEIntegrationProps> = ({
  competitorId,
  competitorName,
  university,
  courseOfStudy,
  onChallengeStart
}) => {
  const [capeStatus, setCapeStatus] = useState<{
    initialized: boolean;
    loading: boolean;
    error: string | null;
  }>({
    initialized: false,
    loading: false,
    error: null
  });

  const [availableTopics, setAvailableTopics] = useState<TaxonomyNode[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<TaxonomyNode | null>(null);
  const [challengeData, setChallengeData] = useState<{
    loading: boolean;
    error: string | null;
    questions: CAPEQuestion[];
    content: CAPEContent[];
  }>({
    loading: false,
    error: null,
    questions: [],
    content: []
  });

  const [testResults, setTestResults] = useState<any>(null);

  // Initialize CAPE and load topics
  useEffect(() => {
    initializeCAPE();
  }, []);

  const testWikipediaAPI = async () => {
    try {
      const result = await capeService.testWikipediaWithProfile('Humanities - Philosophy - Ancient Philosophy');
      setTestResults(result);
      console.log('Wikipedia API Test Result:', result);
    } catch (error) {
      console.error('Wikipedia API Test Failed:', error);
      setTestResults({ success: false, error: error.message });
    }
  };

  const setTestProfile = async () => {
    try {
      const result = await capeService.setTestProfile(competitorId, 'Humanities - Philosophy - Ancient Philosophy');
      console.log('Test Profile Set Result:', result);
      
      // Process the content results directly from the response
      if (result.success && result.data.contentResults) {
        console.log('Content Results:', result.data.contentResults);
        const topics = [];
        
        // Convert content results to topics
        for (const contentResult of result.data.contentResults) {
          console.log(`Processing content result: ${contentResult.interestCategory}`, contentResult);
          
          // If content array is empty, create a fallback topic
          if (!contentResult.content || contentResult.content.length === 0) {
            console.log(`No content found for ${contentResult.interestCategory}, creating fallback topic`);
            topics.push({
              subtopic: contentResult.interestCategory,
              topic: 'Personalized Learning',
              category: 'User Interests',
              subcategory: contentResult.interestCategory,
              difficulty: 'Intermediate',
              skills: ['Recall', 'Apply'],
              tags: ['Personalized', 'Fallback'],
              content_id: `fallback-${Date.now()}-${Math.random()}`,
              metadata: {
                difficulty_default: 'Intermediate',
                learning_objectives: ['Recall', 'Apply']
              },
              fullPath: `User Interests > ${contentResult.interestCategory} > Personalized Learning > Interest-Based Content > ${contentResult.interestCategory}`
            });
          } else {
            // Process actual content
            for (const content of contentResult.content) {
              console.log('Processing content item:', content);
              topics.push({
                subtopic: content.taxonomy_path?.subtopic || contentResult.interestCategory,
                topic: content.taxonomy_path?.topic || 'Personalized Learning',
                category: content.taxonomy_path?.category || 'User Interests',
                subcategory: content.taxonomy_path?.subcategory || contentResult.interestCategory,
                difficulty: content.difficulty || 'Intermediate',
                skills: content.skills || ['Recall', 'Apply'],
                tags: content.tags || ['Personalized'],
                content_id: content.content_id || `test-${Date.now()}`,
                metadata: {
                  difficulty_default: content.difficulty || 'Intermediate',
                  learning_objectives: content.skills || ['Recall', 'Apply'],
                  wikipedia_title: content.content?.metadata?.title,
                  wikipedia_url: content.source_url,
                  wikipedia_summary: content.content?.summary
                },
                fullPath: `${content.taxonomy_path?.category || 'User Interests'} > ${content.taxonomy_path?.subcategory || contentResult.interestCategory} > ${content.taxonomy_path?.topic || 'Personalized Learning'} > ${content.taxonomy_path?.subtopic || contentResult.interestCategory}`
              });
            }
          }
        }
        
        console.log(`Final topics array:`, topics);
        setAvailableTopics(topics);
        setCapeStatus({
          initialized: true,
          loading: false,
          error: null
        });
        
        console.log(`✅ Successfully loaded ${topics.length} topics from test profile`);
      }
    } catch (error) {
      console.error('Set Test Profile Failed:', error);
    }
  };

  const initializeCAPE = async () => {
    setCapeStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Initialize CAPE service
      await capeService.initialize();

      // Process user interest update to fetch personalized content
      await capeService.processUserInterestUpdate(competitorId);

      // Get personalized content based on user interests
      const personalizedContent = await capeService.getPersonalizedContent(competitorId, {
        limit: 6,
        difficulty: 'Intermediate'
      });

      // Convert personalized content to topics
      const topics = personalizedContent.map(content => ({
        subtopic: content.taxonomy_path.subtopic,
        topic: content.taxonomy_path.topic,
        category: content.taxonomy_path.category,
        subcategory: content.taxonomy_path.subcategory,
        difficulty: content.difficulty,
        skills: content.skills,
        tags: content.tags,
        content_id: content.content_id,
        metadata: {
          difficulty_default: content.difficulty,
          learning_objectives: content.skills
        },
        fullPath: `${content.taxonomy_path.category} > ${content.taxonomy_path.subcategory} > ${content.taxonomy_path.topic} > ${content.taxonomy_path.subtopic}`
      }));

      setAvailableTopics(topics);
      setCapeStatus({
        initialized: true,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error initializing CAPE:', error);
      setCapeStatus({
        initialized: false,
        loading: false,
        error: 'Failed to initialize CAPE service with personalized content'
      });
    }
  };

  const handleTopicSelect = async (topic: TaxonomyNode) => {
    setSelectedTopic(topic);
    setChallengeData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Get content and questions for the selected topic
      const result = await capeService.getContentForChallenge({
        subtopic: topic.subtopic,
        difficulty: topic.metadata.difficulty_default as any || 'Intermediate',
        competitorId,
        count: 8,
        questionTypes: ['mcq', 'true_false'],
        kenyaContext: true
      });

      setChallengeData({
        loading: false,
        error: null,
        questions: result.questions,
        content: result.content
      });
    } catch (error) {
      console.error('Error getting challenge content:', error);
      setChallengeData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load challenge content'
      }));
    }
  };

  const handleStartChallenge = () => {
    if (selectedTopic && challengeData.questions.length > 0) {
      onChallengeStart?.({
        topic: selectedTopic,
        questions: challengeData.questions,
        content: challengeData.content
      });
    }
  };

  return (
    <CAPEIntegrationCard>
      <CAPEHeader>
        <CAPETitle>
          <Brain size={24} />
          CAPE Learning Challenge
        </CAPETitle>
      </CAPEHeader>

      <CAPEDescription>
        🎯 Personalized content based on your interests and learning goals. 
        Update your profile to see new topics tailored just for you!
      </CAPEDescription>

      {/* Status Messages */}
      {capeStatus.loading && (
        <StatusMessage type="loading">
          <Loader size={16} />
          Initializing CAPE service...
        </StatusMessage>
      )}

      {capeStatus.error && (
        <StatusMessage type="error">
          <AlertCircle size={16} />
          {capeStatus.error}
        </StatusMessage>
      )}

      {capeStatus.initialized && !capeStatus.loading && (
        <StatusMessage type="success">
          <CheckCircle size={16} />
          CAPE ready! Found {availableTopics.length} relevant topics for {competitorName}
        </StatusMessage>
      )}

      {/* Topic Selection */}
      {capeStatus.initialized && availableTopics.length > 0 && (
        <>
          <h4 style={{ margin: '20px 0 15px 0', fontSize: '1.1rem' }}>
            <Target size={18} style={{ marginRight: '8px' }} />
            Choose Your Challenge Topic
          </h4>

          <TopicGrid>
            {availableTopics.map((topic) => (
              <TopicCard
                key={topic.subtopic}
                selected={selectedTopic?.subtopic === topic.subtopic}
                loading={challengeData.loading && selectedTopic?.subtopic === topic.subtopic}
                onClick={() => !challengeData.loading && handleTopicSelect(topic)}
              >
                <TopicTitle>{topic.subtopic}</TopicTitle>
                <TopicMeta>
                  <div><strong>{topic.category}</strong> → {topic.subcategory}</div>
                  <div>Difficulty: {topic.metadata.difficulty_default || 'Intermediate'}</div>
                  <div>Skills: {topic.metadata.skills?.join(', ') || 'Recall'}</div>
                  {topic.metadata.tags && (
                    <div>Tags: {topic.metadata.tags.slice(0, 2).join(', ')}</div>
                  )}
                </TopicMeta>
              </TopicCard>
            ))}
          </TopicGrid>

          {/* Challenge Data Status */}
          {challengeData.loading && (
            <StatusMessage type="loading">
              <Loader size={16} />
              Loading challenge content for "{selectedTopic?.subtopic}"...
            </StatusMessage>
          )}

          {challengeData.error && (
            <StatusMessage type="error">
              <AlertCircle size={16} />
              {challengeData.error}
            </StatusMessage>
          )}

          {selectedTopic && challengeData.questions.length > 0 && (
            <StatusMessage type="success">
              <CheckCircle size={16} />
              Ready! {challengeData.questions.length} questions and {challengeData.content.length} content items loaded
            </StatusMessage>
          )}

          {/* Start Challenge Button */}
          <ActionButton
            disabled={!selectedTopic || challengeData.loading || challengeData.questions.length === 0}
            onClick={handleStartChallenge}
          >
            {challengeData.loading ? (
              <>
                <Loader size={16} style={{ marginRight: '8px' }} />
                Loading Challenge...
              </>
            ) : (
              <>
                <BookOpen size={16} style={{ marginRight: '8px' }} />
                Start CAPE Challenge
              </>
            )}
          </ActionButton>
        </>
      )}

      {/* Fallback if no topics found */}
      {capeStatus.initialized && availableTopics.length === 0 && (
        <StatusMessage type="error">
          <AlertCircle size={16} />
          No relevant topics found. Try refreshing or contact support.
        </StatusMessage>
      )}

      {/* Test Wikipedia API Button */}
      <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '10px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem' }}>🧪 Test Wikipedia API</h4>
        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', opacity: 0.8 }}>
          Test with profile: "Humanities - Philosophy - Ancient Philosophy"
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <ActionButton onClick={testWikipediaAPI} style={{ width: 'auto', margin: '0' }}>
            Test Wikipedia API
          </ActionButton>
          <ActionButton onClick={setTestProfile} style={{ width: 'auto', margin: '0', background: 'rgba(46, 204, 113, 0.9)', color: 'white' }}>
            Set Test Profile
          </ActionButton>
        </div>
        
        {testResults && (
          <div style={{ marginTop: '15px' }}>
            {testResults.success ? (
              <StatusMessage type="success">
                <CheckCircle size={16} />
                ✅ Wikipedia API Test Successful! Found {testResults.data?.wikipediaResults?.totalFound || 0} articles
              </StatusMessage>
            ) : (
              <StatusMessage type="error">
                <AlertCircle size={16} />
                ❌ Test Failed: {testResults.error || 'Unknown error'}
              </StatusMessage>
            )}
          </div>
        )}
      </div>
    </CAPEIntegrationCard>
  );
};

export default CAPEIntegration;
